// ** UI
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// ** Components
import Link from '@/components/common/Link';
import StrapiImageRenderer from '@/components/strapi/StrapiImageRenderer';

// ** Types
import type { UploadFile } from '@/types/graphql';
import type { AccordionSubComponentProps } from './index';

const InnungElementComponent = ({ accordionElementTypeInnung, host }: AccordionSubComponentProps) => {
	const {
		innungCity,
		innungEmail,
		innungName,
		innungPostalCode,
		innungStreet,
		innungTelephone,
		innungUrl,
		obermeister,
		obermeisterName,
		obermeisterCompany,
		obermeisterCompanyCity,
		obermeisterCompanyEmail,
		obermeisterCompanyPostalCode,
		obermeisterCompanyStreet,
		obermeisterCompanyTelephone,
		obermeisterCompanyWebsite,
		innungLogo
	} = accordionElementTypeInnung?.accordionElementInnung ?? {};

	const { firstName = '', lastName = '' } = obermeister || {};
	const fullName = firstName || lastName ? `${firstName} ${lastName}`.trim() : obermeisterName;

	return (
		<Grid container spacing={6} sx={{ pb: 6, pt: 2 }}>
			{/* Logo Section */}
			<Grid size={{ xs: 12, md: 3 }}>
				<StrapiImageRenderer
					host={host}
					imageData={(innungLogo as UploadFile) || '/images/placeholders/image200.png'}
					alt="Innung Logo"
					height={160}
					width={160}
					sizes="(max-width:600px) 100vw, 160px"
					style={{
						width: '100%',
						maxWidth: '160px',
						height: 'auto',
						objectFit: 'contain'
					}}
				/>
			</Grid>

			{/* Obermeister Info */}
			<Grid size={{ xs: 12, md: 4.5 }}>
				<Stack spacing={2}>
					<Box>
						<Typography variant="subheadline-normal" sx={{ fontWeight: 700 }}>
							Obermeister:
						</Typography>
						<Typography variant="body-md">{fullName}</Typography>
					</Box>
					<Box>
						<Typography variant="body-md" sx={{ fontWeight: 600 }}>
							{obermeisterCompany}
						</Typography>
						<Typography variant="body-md">{obermeisterCompanyStreet}</Typography>
						<Typography variant="body-md">{`${obermeisterCompanyPostalCode} ${obermeisterCompanyCity}`}</Typography>
					</Box>
					<Stack spacing={0.5}>
						<Link href={`tel:${obermeisterCompanyTelephone}`} underline>
							<Typography variant="body-md">{obermeisterCompanyTelephone}</Typography>
						</Link>
						<Link href={`mailto:${obermeisterCompanyEmail}`} underline target="_blank">
							<Typography variant="body-md">{obermeisterCompanyEmail}</Typography>
						</Link>
						{obermeisterCompanyWebsite && (
							<Link href={obermeisterCompanyWebsite} underline target="_blank">
								<Typography variant="body-md">{obermeisterCompanyWebsite.replace(/^https?:\/\//, '')}</Typography>
							</Link>
						)}
					</Stack>
				</Stack>
			</Grid>

			{/* Geschäftsstelle Info */}
			<Grid size={{ xs: 12, md: 4.5 }}>
				<Stack spacing={2}>
					<Box>
						<Typography variant="subheadline-normal" sx={{ fontWeight: 700 }}>
							Geschäftsstelle:
						</Typography>
						<Typography variant="body-md">{innungName}</Typography>
						<Typography variant="body-md">{innungStreet}</Typography>
						<Typography variant="body-md">{`${innungPostalCode} ${innungCity}`}</Typography>
					</Box>
					<Stack spacing={0.5}>
						<Link href={`tel:${innungTelephone}`} underline>
							<Typography variant="body-md">{innungTelephone}</Typography>
						</Link>
						<Link href={`mailto:${innungEmail}`} underline target="_blank">
							<Typography variant="body-md">{innungEmail}</Typography>
						</Link>
						{innungUrl && (
							<Link href={innungUrl} underline target="_blank">
								<Typography variant="body-md">{innungUrl.replace(/^https?:\/\//, '')}</Typography>
							</Link>
						)}
					</Stack>
				</Stack>
			</Grid>
		</Grid>
	);
};

export default InnungElementComponent;
